// ---------------------------------------------------------------------------
// Agency Churn Cohorts — AN-023 Phase 1 (schema only)
// ---------------------------------------------------------------------------
// Story AN-023: "Agency roll-up dashboard summarizes all clients with health,
// urgent issues, retention/churn, and per-service profitability."
//
// Per docs/vision_execution/openclaw/AN-023-DECISION-PACKET.md the churn /
// retention half of AN-023 needs a cohort projection keyed by
// (tenant_id, cohort_quarter) so the future `queryAgencyChurn(orgId,
// quarterStart)` rollup can return per-tenant + org-aggregate quarterly
// churn without scanning the full `service_memberships` lifecycle on every
// dashboard load.
//
// The packet specifies (verbatim):
//
//   cohort_quarter      = date_trunc('quarter', start_date)
//   starting_count      = active+trialing at quarter start
//   lost_count          = transitioned to canceled/expired during the quarter
//   churn_rate          = lost_count / starting_count
//
// Phase 1 scope (this file): persistence schema only. The cohort
// materialiser, the `queryAgencyChurn` rollup, the `AgencyMetrics`
// surface extension (`churnRate`/`retentionRate`/`cohortQuarter`), and the
// `subscription.status_changed@v1` CQRS event wiring all land in later
// phases governed by the decision packet.
//
// Design rules locked here:
// - One row per (tenant_id, cohort_quarter). The materialiser writes a fresh
//   row on quarter boundary, or upserts the in-progress quarter on each
//   `subscription.status_changed@v1` event. The unique index enforces
//   idempotency so re-running the materialiser cannot duplicate cohorts.
// - All counts are integer cents/counts; the churn ratio is recomputed by
//   the rollup query rather than stored, so a backfill that adjusts
//   `lost_count` cannot drift from a stale ratio.
// - The `agency_org_id` denormalisation column lets the agency dashboard
//   query `WHERE agency_org_id = $1` directly without re-resolving the
//   sub-account → agency relationship per row; the materialiser writes it
//   from `getOrgTenants` at the moment the cohort is sealed.
// - GDPR posture: aggregated business metric, no personal data. Cascade by
//   tenant_id alongside the rest of the `service_memberships` lineage.
// ---------------------------------------------------------------------------

import {
  check,
  index,
  integer,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

/**
 * Closed lifecycle vocabulary for the sealed-vs-in-progress state of a cohort
 * row. `in_progress` is the current quarter — the materialiser is still
 * folding `subscription.status_changed@v1` events into `lost_count`.
 * `sealed` is a closed quarter; the row is immutable except for a corrective
 * backfill which MUST emit
 * `analytics.agency_churn_cohort_corrected@v1` (event lands in a later phase).
 */
export const AGENCY_CHURN_COHORT_LIFECYCLES = [
  "in_progress",
  "sealed",
] as const;

export type AgencyChurnCohortLifecycle =
  (typeof AGENCY_CHURN_COHORT_LIFECYCLES)[number];

export const agencyChurnCohorts = pgTable(
  "agency_churn_cohorts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    /**
     * Sub-account tenant whose `service_memberships` cohort this row
     * summarises. Tenant-scoped so an agency dashboard query stays inside
     * the tenant boundary while joining to `getOrgTenants` for the agency
     * roll-up.
     */
    tenantId: uuid("tenant_id").notNull(),
    /**
     * Denormalised agency org_id so the agency dashboard can filter by
     * agency without a join. Materialiser writes it at seal time from the
     * org-membership snapshot; nullable for tenants that are not part of
     * any agency org (direct-sale tenants), which still need their own
     * churn history.
     */
    agencyOrgId: uuid("agency_org_id"),
    /**
     * Quarter start, UTC, midnight on the first day of the quarter
     * (Jan 1 / Apr 1 / Jul 1 / Oct 1). The materialiser computes this as
     * `date_trunc('quarter', start_date)` so the value is deterministic
     * across re-runs.
     */
    cohortQuarter: timestamp("cohort_quarter", tz).notNull(),
    /**
     * Subscriptions that were `active` or `trialing` at quarter start.
     * This is the cohort denominator for `churn_rate`.
     */
    startingCount: integer("starting_count").notNull(),
    /**
     * Subscriptions that transitioned to `canceled` or `expired` during the
     * quarter. This is the cohort numerator for `churn_rate`.
     */
    lostCount: integer("lost_count").notNull(),
    /**
     * Subscriptions that survived the quarter as `active`/`trialing` and
     * did not transition to `past_due` either. Surfaced as
     * `retentionRate = retained_count / starting_count` by the rollup.
     */
    retainedCount: integer("retained_count").notNull(),
    /**
     * Lifecycle marker — see AGENCY_CHURN_COHORT_LIFECYCLES.
     */
    lifecycle: text("lifecycle").notNull().default("in_progress"),
    /**
     * When the materialiser sealed the cohort. NULL while
     * `lifecycle = 'in_progress'`.
     */
    sealedAt: timestamp("sealed_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    /**
     * Row identity: one cohort row per (tenant, quarter). The materialiser
     * upserts on this key so a re-run of the same quarter cannot produce a
     * duplicate.
     */
    uniqueIndex("agency_churn_cohorts_tenant_quarter_uq").on(
      table.tenantId,
      table.cohortQuarter,
    ),
    /**
     * Agency dashboard hot path: scan the latest sealed quarter across all
     * sub-accounts of an agency.
     */
    index("agency_churn_cohorts_agency_quarter_idx").on(
      table.agencyOrgId,
      table.cohortQuarter,
    ),
    /**
     * Per-tenant audit ordering for the drill-down view.
     */
    index("agency_churn_cohorts_tenant_quarter_idx").on(
      table.tenantId,
      table.cohortQuarter,
    ),
    check(
      "agency_churn_cohorts_starting_nonneg_ck",
      sql`${table.startingCount} >= 0`,
    ),
    check(
      "agency_churn_cohorts_lost_nonneg_ck",
      sql`${table.lostCount} >= 0`,
    ),
    check(
      "agency_churn_cohorts_retained_nonneg_ck",
      sql`${table.retainedCount} >= 0`,
    ),
    /**
     * Lost + retained cannot exceed the starting count. Equality holds when
     * the cohort is fully observed; strict inequality holds while the
     * quarter is still in progress.
     */
    check(
      "agency_churn_cohorts_lost_le_starting_ck",
      sql`${table.lostCount} + ${table.retainedCount} <= ${table.startingCount}`,
    ),
    check(
      "agency_churn_cohorts_lifecycle_ck",
      sql`${table.lifecycle} IN ('in_progress','sealed')`,
    ),
    /**
     * Sealed cohorts must carry a sealed timestamp; in-progress cohorts
     * must NOT. Prevents drift between the lifecycle flag and the audit
     * timestamp.
     */
    check(
      "agency_churn_cohorts_sealed_timestamp_ck",
      sql`(${table.lifecycle} = 'sealed' AND ${table.sealedAt} IS NOT NULL) OR (${table.lifecycle} = 'in_progress' AND ${table.sealedAt} IS NULL)`,
    ),
  ],
);
